import { sleep } from '../utils/importUtils';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AccountingEngine } from './accountingEngine';

// Hardcoded fallback — used ONLY when no company DB is loaded.
export const AVAILABLE_BANKS: string[] = [];

/**
 * Fetch bank + cash ledger names from the loaded company DB.
 */
export async function fetchBankAccounts(): Promise<string[]> {
  const company = useStore.getState().activeCompany;
  if (!company) {
    console.warn('[ImportBank] fetchBankAccounts: No active company found.');
    return [];
  }
  
  try {
    const ledgers = await db.ledgers.where('companyId').equals(company.id!).toArray();
    const groups = await db.groups.where('companyId').equals(company.id!).toArray();
    
    const targetGroupIds = new Set<number>();
    
    groups.forEach(g => {
      const name = g.name.toLowerCase();
      if (name.includes('bank') || name.includes('cash in hand') || name.includes('cash-in-hand')) {
        targetGroupIds.add(g.id!);
      }
    });
    
    // Resolve subgroups
    let added = true;
    while(added) {
      added = false;
      for (const g of groups) {
        if (!targetGroupIds.has(g.id!) && g.parentGroupId && targetGroupIds.has(g.parentGroupId)) {
          targetGroupIds.add(g.id!);
          added = true;
        }
      }
    }
    
    const banks = ledgers
      .filter((l: any) => !l.isDeleted)
      .filter((l: any) => {
        if (l.name.toLowerCase().includes('bank') || l.name.toLowerCase() === 'cash') return true;
        if (l.groupId !== null && targetGroupIds.has(l.groupId)) return true;
        return false;
      })
      .map((l: any) => l.name as string)
      .sort();
      
    if (banks.length > 0) return banks;
    return ledgers.filter(l => !l.isDeleted).map(l => l.name).sort();
  } catch (e) {
    console.warn(`[ImportBank] fetchBankAccounts failed:`, e);
  }
  return [];
}

const validDate = (y: number, m: number, d: number) => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  // JS Date overflows invalid days (e.g. Feb 30 → Mar 2). Verify it didn't shift.
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d ? dt : null;
};

export const DATE_FORMATS = [
  { re: /^(\d{4})-(\d{2})-(\d{2})$/, parse: (m: any) => validDate(+m[1], +m[2], +m[3]) },
  { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, parse: (m: any) => validDate(+m[3], +m[2], +m[1]) },
  { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, parse: (m: any) => validDate(+m[3], +m[2], +m[1]) },
];

export const PIPELINE_STEPS = ['Structure Validation', 'Row Processing', 'Duplicate Checking', 'Render View'];
export const CHUNK_SIZE = 75;

// ─── Ledger suggestion rules ──────────────────────────────────────────────────
export const LEDGER_SUGGESTION_RULES = [
  { test: (t: string, s: string) => s === 'debit' && /fuel|petrol|diesel/i.test(t), ledger: 'Fuel Expense' },
  { test: (t: string, s: string) => s === 'debit' && /rent|lease|property/i.test(t), ledger: 'Rent Account' },
  { test: (t: string, s: string) => s === 'debit' && /salary|wages|payroll|staff/i.test(t), ledger: 'Salary Account' },
  { test: (t: string, s: string) => s === 'debit' && /advert|marketing|campaign|promo/i.test(t), ledger: 'Advertising Expense' },
  { test: (t: string, s: string) => s === 'debit' && /software|saas|subscription|license/i.test(t), ledger: 'Software Subscriptions' },
  { test: (t: string, s: string) => s === 'debit' && /legal|advocate|court|lawyer/i.test(t), ledger: 'Legal Services' },
  { test: (t: string, s: string) => s === 'debit' && /consult|advisory|professional/i.test(t), ledger: 'Consulting Fees' },
  { test: (t: string, s: string) => s === 'debit' && /office|stationery|supply|supplies/i.test(t), ledger: 'Office Maintenance' },
  { test: (t: string, s: string) => s === 'debit' && /insurance|premium/i.test(t), ledger: 'Insurance Premium' },
  { test: (t: string, s: string) => s === 'debit' && /freight|transport|courier|logistics/i.test(t), ledger: 'Freight Charges' },
  { test: (t: string, s: string) => s === 'debit' && /repair|maintenance|service/i.test(t), ledger: 'Repairs & Maintenance' },
  { test: (t: string, s: string) => s === 'debit' && /print|stationery|paper/i.test(t), ledger: 'Printing & Stationery' },
  { test: (t: string, s: string) => s === 'credit' && /sale|revenue|customer|client/i.test(t), ledger: 'Sales Account' },
  { test: (t: string, s: string) => s === 'credit' && /commission|brokerage/i.test(t), ledger: 'Commission Income' },
  { test: (t: string, s: string) => s === 'credit' && /interest|dividend|return/i.test(t), ledger: 'Interest Income' },
  { test: (t: string, s: string) => s === 'debit' && /ltd|pvt|private|limited|llp|inc/i.test(t), ledger: 'Sundry Creditors', partyOnly: true },
  { test: (t: string, s: string) => s === 'credit' && /ltd|pvt|private|limited|llp|inc/i.test(t), ledger: 'Sundry Debtors', partyOnly: true },
  { test: (t: string, s: string) => /purchase|buy|vendor|supplier/i.test(t), ledger: 'Purchase Expenses' },
  { test: (t: string, s: string) => /misc|general/i.test(t), ledger: 'Miscellaneous Expenses' },
  { test: (t: string, s: string) => /bank charge|bank fee|service charge/i.test(t), ledger: 'Bank Charges' },
  { test: (t: string, s: string) => /tax|gst|tds|income tax/i.test(t), ledger: 'Tax Payable' },
];

export function suggestLedger(party: string, narration: string = '', side: string = '') {
  if (!party) return null;
  for (const rule of LEDGER_SUGGESTION_RULES) {
    if (rule.test(party, side)) return rule.ledger;
  }
  if (narration) {
    for (const rule of LEDGER_SUGGESTION_RULES) {
      if (!(rule as any).partyOnly && rule.test(narration, side)) return rule.ledger;
    }
  }
  return null;
}

// ─── Ledger Memory ────────────────────────────────────────────────────────────
const PARTY_LEDGER_MEMORY: Record<string, string> = {};

export async function fetchLedgerMemory() {
  await sleep(100);
  return { ...PARTY_LEDGER_MEMORY };
}

export async function saveLedgerMemory(party: string, ledger: string) {
  if (party && ledger) PARTY_LEDGER_MEMORY[party.toUpperCase()] = ledger;
}

// ─── Fetch real ledgers from window.db ────────────────────────────────────────
export async function fetchLedgers(): Promise<string[]> {
  const company = useStore.getState().activeCompany;
  if (!company) return [];
  
  try {
    const ledgers = await db.ledgers.where('companyId').equals(company.id!).toArray();
    const names = ledgers
      .filter((l: any) => !l.isDeleted)
      .map((l: any) => l.name as string)
      .sort();
      
    if (names.length > 0) return names;
  } catch (e) {
    console.warn(`[ImportBank] fetchLedgers failed:`, e);
  }
  return [
    'Sales Account', 'Purchase Expenses', 'Salary Account', 'Rent Account',
    'Miscellaneous Expenses', 'Consulting Fees', 'Office Maintenance',
    'Advertising Expense', 'Software Subscriptions', 'Legal Services',
    'Fuel Expense', 'Bank Charges', 'Interest Income', 'Sundry Debtors',
    'Sundry Creditors', 'Capital Account', 'Drawings', 'Loan Account',
    'Tax Payable', 'Depreciation', 'Insurance Premium', 'Freight Charges',
    'Commission Income', 'Repairs & Maintenance', 'Printing & Stationery',
  ];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
async function getLedgerByTerm(companyId: number, name: string): Promise<any> {
    if (!name?.trim()) return null;
    const ledgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    const active = ledgers.filter((l: any) => !l.isDeleted);
    const nameLower = name.trim().toLowerCase();

    // 1. Exact match (case-insensitive)
    const exact = active.find((l: any) => l.name?.trim().toLowerCase() === nameLower);
    if (exact) return exact;

    // 2. Short name match — strip em-dash suffix (e.g. "HDFC Bank — Current")
    const shortName = nameLower.split('—')[0].trim();
    const shortMatch = active.find((l: any) => l.name?.trim().toLowerCase() === shortName);
    if (shortMatch) return shortMatch;

    // 3. Prefer ledger whose name *starts with* the search term (less greedy than includes)
    const startsMatch = active.find((l: any) => l.name?.trim().toLowerCase().startsWith(nameLower));
    if (startsMatch) return startsMatch;

    // 4. Substring match — search term fully contained in ledger name, but only if unique
    const subMatches = active.filter((l: any) => l.name && l.name.toLowerCase().includes(nameLower));
    if (subMatches.length === 1) return subMatches[0];

    // 5. Ledger name fully contained in search term (abbreviation typed by user)
    const reverseMatches = active.filter((l: any) => l.name && nameLower.includes(l.name.toLowerCase()));
    if (reverseMatches.length === 1) return reverseMatches[0];

    return null;
}

function ddmmyyyyToISO(date: string): string {
  if (!date) return new Date().toISOString().split('T')[0];
  if (date.includes('/')) {
    const [dd, mm, yyyy] = date.split('/');
    if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  if (date.includes('-')) {
    if (date.length === 10 && date[4] === '-') return date; // already ISO: YYYY-MM-DD
    const [dd, mm, yyyy] = date.split('-');
    if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  return date;
}

// ─── Real voucher creation ────────────────────────────────────────────────────
export async function apiCreateVouchers(
  payloads: any[]
): Promise<{ success: boolean; rowIdx: number; refId: string | null; error?: string }[]> {
  const results: { success: boolean; rowIdx: number; refId: string | null; error?: string }[] = [];

  const company = useStore.getState().activeCompany;
  if (!company) {
      return payloads.map(p => ({ success: false, rowIdx: p.rowIdx, refId: null, error: 'No active company found.' }));
  }

  for (const p of payloads) {
    try {
      // ── Pre-flight validations ──────────────────────────────────────────────
      const debitAmt  = Number(p.debit)  || 0;
      const creditAmt = Number(p.credit) || 0;

      // Guard: both columns filled → ambiguous row, refuse
      if (debitAmt > 0 && creditAmt > 0) {
        throw new Error(`Row has both Debit (${debitAmt}) and Credit (${creditAmt}) filled. Only one should be non-zero.`);
      }

      // Guard: zero / negative amount
      const amount = debitAmt > 0 ? debitAmt : creditAmt;
      if (!amount || amount <= 0) {
        throw new Error(`Amount must be greater than zero (got Debit=${debitAmt}, Credit=${creditAmt}).`);
      }

      // Debit entry (outflow) → Payment voucher
      // Credit entry (inflow) → Receipt voucher
      const isDebit = debitAmt > 0;
      const voucherType = isDebit ? 'Payment' : 'Receipt';

      const bankLedger = await getLedgerByTerm(company.id!, p.bankAccount?.name || '');
      if (!bankLedger) throw new Error(`Bank ledger "${p.bankAccount?.name || ''}" not found in chart of accounts.`);
      
      const matchedLedger = await getLedgerByTerm(company.id!, p.matchedLedger);
      if (!matchedLedger) throw new Error(`Ledger not found: "${p.matchedLedger}".`);

      // Resolve voucherTypeId for correct numbering
      const vt = await db.voucherTypes
        .where('[companyId+name]').equals([company.id!, voucherType]).first();

      // Double-entry:
      //   PAYMENT (debit/outflow): Matched Dr | Bank Cr
      //   RECEIPT (credit/inflow): Bank Dr    | Matched Cr
      const ledgerEntries = voucherType === 'Payment'
        ? [
            { ledgerId: matchedLedger.id!, amount, type: 'Dr' as const },
            { ledgerId: bankLedger.id!,    amount, type: 'Cr' as const },
          ]
        : [
            { ledgerId: bankLedger.id!,    amount, type: 'Dr' as const },
            { ledgerId: matchedLedger.id!, amount, type: 'Cr' as const },
          ];

      // ── Explicit Dr = Cr assertion (defence-in-depth before DB write) ───────
      const totalDr = ledgerEntries.filter(e => e.type === 'Dr').reduce((s, e) => s + e.amount, 0);
      const totalCr = ledgerEntries.filter(e => e.type === 'Cr').reduce((s, e) => s + e.amount, 0);
      if (Math.abs(totalDr - totalCr) >= 0.01) {
        throw new Error(`Double-entry imbalance: Dr ₹${totalDr.toFixed(2)} ≠ Cr ₹${totalCr.toFixed(2)}. Please report this bug.`);
      }

      const vouchObj = {
        companyId: company.id!,
        type: voucherType,
        voucherTypeId: vt?.id,
        date: ddmmyyyyToISO(p.date),
        narration: p.narration || `Imported ${voucherType} — ${p.party}`,
      };

      const newId = await AccountingEngine.postVoucher(vouchObj, ledgerEntries);
      const newVoucher = await db.vouchers.get(newId);

      results.push({ success: true, rowIdx: p.rowIdx, refId: newVoucher?.voucherNumber || String(newId) });
    } catch (e: any) {
      console.error('[ImportBank] apiCreateVouchers error:', e);
      results.push({ success: false, rowIdx: p.rowIdx, refId: null, error: e.message });
    }
  }

  // Trigger db updated
  window.dispatchEvent(new Event('db-updated'));

  return results;
}